import React, { useRef, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Page from "../components/Page";
import {
  IconCreditCard,
  IconReceipt2,
  IconCash,
  IconUser,
  IconCalendar,
  IconX,
  IconTrendingDown,
  IconTruckDelivery,
  IconSearch,
  IconFileSpreadsheet,
  IconFileDownload,
  IconPrinter,
  IconArrowLeft,
  IconCheck,
  IconAlertCircle,
  IconBuildingStore,
  IconPhone,
  IconMail,
  IconId,
  IconLayersSubtract,
  IconFilter,
  IconCoins,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import {
  usePurchases,
  paySupplierDue,
  bulkPaySupplierDue,
  useSuppliers,
  createPurchase,
  useSupplierDues,
  useSupplierStatement
} from "../controllers/inventory.controller";
import { usePaymentTypes, useStoreSettings } from "../controllers/settings.controller";
import { CURRENCIES } from "../config/currencies.config";
import { toast } from "react-hot-toast";
import {
  exportStatementToPDF,
  exportStatementToExcel,
  printStatementHTML
} from "../helpers/StatementExportHelper";

export default function AccountsPayablePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // View state: "suppliers" | "purchases" | "statement"
  const [activeTab, setActiveTab] = useState("suppliers");
  const [selectedSupplierForStatement, setSelectedSupplierForStatement] = useState(null);

  // Search & Filter
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState({
    from: "",
    to: "",
  });

  // Pagination states
  const [supplierPage, setSupplierPage] = useState(1);
  const [supplierPageSize, setSupplierPageSize] = useState(12);

  const [billsPage, setBillsPage] = useState(1);
  const [billsPageSize, setBillsPageSize] = useState(20);

  const [statementPage, setStatementPage] = useState(1);
  const [statementPageSize, setStatementPageSize] = useState(25);

  // Modal states
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedSupplierForBulkPay, setSelectedSupplierForBulkPay] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().substring(0, 10));
  const [payMethod, setPayMethod] = useState("Cash");
  const [payNotes, setPayNotes] = useState("");
  const [isSubmittingPay, setIsSubmittingPay] = useState(false);

  // Manual Due modal state
  const [manualDueForm, setManualDueForm] = useState({
    supplier_id: "",
    amount: "",
    date: new Date().toISOString().substring(0, 10),
    notes: "Manual Due Entry"
  });

  // Data fetching
  const { data: storeSettings } = useStoreSettings();
  const currencySymbol = useMemo(() => {
    const cur = CURRENCIES.find((c) => c.cc === storeSettings?.currency);
    return cur?.symbol || storeSettings?.currency || "Rs.";
  }, [storeSettings]);

  const { data: purchases, mutate: mutatePurchases, isLoading: isLoadingPurchases } = usePurchases({
    from: dateFilter.from || null,
    to: dateFilter.to || null
  });

  const { data: supplierDues, mutate: mutateSupplierDues, isLoading: isLoadingSupplierDues } = useSupplierDues(search);
  const { data: paymentTypes } = usePaymentTypes();
  const { data: suppliers } = useSuppliers();

  const supplierList = useMemo(() => {
    if (!suppliers) return [];
    if (Array.isArray(suppliers)) return suppliers;
    if (Array.isArray(suppliers.suppliers)) return suppliers.suppliers;
    return [];
  }, [suppliers]);

  // Statement data for selected supplier
  const {
    data: statementData,
    isLoading: isLoadingStatement,
    mutate: mutateStatement
  } = useSupplierStatement(
    selectedSupplierForStatement?.id || null,
    dateFilter.from,
    dateFilter.to
  );

  // Read URL query parameter for direct supplier ledger link
  useEffect(() => {
    const sId = searchParams.get("supplierId");
    if (sId && supplierList && supplierList.length > 0) {
      const match = supplierList.find((s) => String(s.id) === String(sId));
      if (match) {
        setSelectedSupplierForStatement(match);
        setActiveTab("statement");
      }
    }
  }, [searchParams, supplierList]);

  // Filtered purchases
  const duePurchases = useMemo(() => {
    if (!purchases || !Array.isArray(purchases)) return [];
    return purchases.filter((p) => {
      const due = Number(String(p.due_amount).replace(/,/g, "")) || 0;
      if (due <= 0) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        p.supplier_name?.toLowerCase().includes(q) ||
        p.purchase_invoice_no?.toLowerCase().includes(q) ||
        String(p.id).includes(q)
      );
    });
  }, [purchases, search]);

  // Pagination computations
  const totalSupplierPages = Math.max(1, Math.ceil((supplierDues?.length || 0) / supplierPageSize));
  const safeSupplierPage = Math.min(supplierPage, totalSupplierPages);
  const paginatedSupplierDues = useMemo(() => {
    return (supplierDues || []).slice((safeSupplierPage - 1) * supplierPageSize, safeSupplierPage * supplierPageSize);
  }, [supplierDues, safeSupplierPage, supplierPageSize]);

  const totalBillsPages = Math.max(1, Math.ceil((duePurchases?.length || 0) / billsPageSize));
  const safeBillsPage = Math.min(billsPage, totalBillsPages);
  const paginatedDuePurchases = useMemo(() => {
    return (duePurchases || []).slice((safeBillsPage - 1) * billsPageSize, safeBillsPage * billsPageSize);
  }, [duePurchases, safeBillsPage, billsPageSize]);

  const statementTransactions = statementData?.transactions || [];
  const totalStatementPages = Math.max(1, Math.ceil(statementTransactions.length / statementPageSize));
  const safeStatementPage = Math.min(statementPage, totalStatementPages);
  const paginatedStatementTransactions = useMemo(() => {
    return statementTransactions.slice((safeStatementPage - 1) * statementPageSize, safeStatementPage * statementPageSize);
  }, [statementTransactions, safeStatementPage, statementPageSize]);

  // High-level Metrics
  const metrics = useMemo(() => {
    let totalLiabilities = 0;
    let totalPurchased = 0;
    let totalPaid = 0;

    if (purchases && Array.isArray(purchases)) {
      purchases.forEach((p) => {
        const total = (Number(String(p.total_amount).replace(/,/g, "")) || 0) - (Number(String(p.discount_amount).replace(/,/g, "")) || 0);
        const paid = Number(String(p.paid_amount).replace(/,/g, "")) || 0;
        const due = Number(String(p.due_amount).replace(/,/g, "")) || 0;
        totalPurchased += total;
        totalPaid += paid;
        totalLiabilities += due;
      });
    }

    const suppliersWithDue = supplierDues ? supplierDues.length : 0;

    return { totalLiabilities, totalPurchased, totalPaid, suppliersWithDue };
  }, [purchases, supplierDues]);

  // FIFO Allocation calculation in real-time for bulk payment
  const fifoAllocations = useMemo(() => {
    if (!selectedSupplierForBulkPay) return [];
    const amountToApply = parseFloat(String(payAmount).replace(/,/g, "")) || 0;
    if (amountToApply <= 0) return [];

    // Filter unpaid purchases for this supplier sorted oldest first
    const supplierPurchases = (purchases || [])
      .filter((p) => Number(p.supplier_id) === Number(selectedSupplierForBulkPay.id) && (Number(String(p.due_amount).replace(/,/g, "")) || 0) > 0)
      .sort((a, b) => {
        if (a.purchase_invoice_no === "OPENING-BALANCE") return -1;
        if (b.purchase_invoice_no === "OPENING-BALANCE") return 1;
        return new Date(a.purchase_date) - new Date(b.purchase_date);
      });

    let remaining = amountToApply;
    const allocations = [];

    for (const p of supplierPurchases) {
      if (remaining <= 0) break;
      const due = Number(String(p.due_amount).replace(/,/g, "")) || 0;
      const allocated = Math.min(remaining, due);
      const remainingDue = Math.max(0, due - allocated);
      const isCleared = remainingDue <= 0;

      allocations.push({
        purchase_id: p.id,
        invoice_no: p.purchase_invoice_no || `PUR-#${p.id}`,
        date: p.purchase_date,
        original_due: due,
        allocated,
        remaining_due: remainingDue,
        is_cleared: isCleared
      });

      remaining -= allocated;
    }

    return allocations;
  }, [selectedSupplierForBulkPay, payAmount, purchases]);

  // Open single purchase payment modal
  const btnShowSinglePayModal = (purchase) => {
    setSelectedPurchase(purchase);
    setSelectedSupplierForBulkPay(null);
    setPayAmount(purchase.due_amount);
    setPayNotes("");
    document.getElementById("modal-pay-due").showModal();
  };

  // Open bulk FIFO payment modal
  const btnShowBulkPayModal = (supplier) => {
    setSelectedSupplierForBulkPay(supplier);
    setSelectedPurchase(null);
    setPayAmount(supplier.total_due || "");
    setPayNotes("");
    document.getElementById("modal-bulk-pay-fifo").showModal();
  };

  // Process single purchase payment
  const btnProcessSinglePayment = async () => {
    if (!selectedPurchase || !payAmount || parseFloat(payAmount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      setIsSubmittingPay(true);
      toast.loading("Processing payment...");
      const res = await paySupplierDue({
        purchase_id: selectedPurchase.id,
        amount: parseFloat(payAmount),
        payment_date: payDate,
        payment_method: payMethod,
        notes: payNotes || `Payment for Bill #${selectedPurchase.purchase_invoice_no || selectedPurchase.id}`
      });

      toast.dismiss();
      toast.success("Payment recorded successfully!");
      mutatePurchases();
      mutateSupplierDues();
      if (selectedSupplierForStatement) mutateStatement();
      document.getElementById("modal-pay-due").close();
      setSelectedPurchase(null);
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error processing payment");
    } finally {
      setIsSubmittingPay(false);
    }
  };

  // Process Bulk FIFO Payment
  const btnProcessBulkFIFOPayment = async () => {
    if (!selectedSupplierForBulkPay || !payAmount || parseFloat(payAmount) <= 0) {
      toast.error("Please enter a valid settlement amount");
      return;
    }

    try {
      setIsSubmittingPay(true);
      toast.loading("Settling supplier dues via FIFO...");
      const res = await bulkPaySupplierDue({
        supplier_id: selectedSupplierForBulkPay.id,
        amount: parseFloat(payAmount),
        payment_date: payDate,
        payment_method: payMethod,
        notes: payNotes || "FIFO Bulk Settlement"
      });

      toast.dismiss();
      toast.success(res?.data?.message || "Dues settled with FIFO successfully!");
      mutatePurchases();
      mutateSupplierDues();
      if (selectedSupplierForStatement) mutateStatement();
      document.getElementById("modal-bulk-pay-fifo").close();
      setSelectedSupplierForBulkPay(null);
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error processing FIFO settlement");
    } finally {
      setIsSubmittingPay(false);
    }
  };

  // Process Manual Due
  const btnProcessManualDue = async () => {
    if (!manualDueForm.supplier_id || !manualDueForm.amount || parseFloat(manualDueForm.amount) <= 0) {
      toast.error("Please select a supplier and valid amount");
      return;
    }
    try {
      toast.loading("Recording manual due...");
      const res = await createPurchase({
        supplier_id: manualDueForm.supplier_id,
        purchase_date: manualDueForm.date,
        total_amount: parseFloat(manualDueForm.amount),
        paid_amount: 0,
        purchase_invoice_no: manualDueForm.notes || "MANUAL-DUE",
        items: []
      });

      toast.dismiss();
      toast.success("Manual due recorded successfully!");
      mutatePurchases();
      mutateSupplierDues();
      if (selectedSupplierForStatement) mutateStatement();
      document.getElementById("modal-manual-due").close();
      setManualDueForm({
        supplier_id: "",
        amount: "",
        date: new Date().toISOString().substring(0, 10),
        notes: "Manual Due Entry"
      });
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error adding manual due");
    }
  };

  // Open statement view for a supplier
  const btnOpenStatement = (supplier) => {
    setSelectedSupplierForStatement(supplier);
    setActiveTab("statement");
  };

  // Export handlers
  const handleExportPDF = () => {
    if (!statementData || !statementData.supplier) {
      toast.error("No statement data to export");
      return;
    }
    exportStatementToPDF({
      type: "Supplier",
      party: statementData.supplier,
      storeSettings,
      transactions: statementData.transactions || [],
      summary: statementData.summary || {},
      dateRange: dateFilter.from && dateFilter.to ? `${dateFilter.from} to ${dateFilter.to}` : "All Time",
      currency: currencySymbol
    });
  };

  const handleExportExcel = () => {
    if (!statementData || !statementData.supplier) {
      toast.error("No statement data to export");
      return;
    }
    exportStatementToExcel({
      type: "Supplier",
      party: statementData.supplier,
      storeSettings,
      transactions: statementData.transactions || [],
      summary: statementData.summary || {},
      dateRange: dateFilter.from && dateFilter.to ? `${dateFilter.from} to ${dateFilter.to}` : "All Time",
      currency: currencySymbol
    });
  };

  const handlePrint = () => {
    if (!statementData || !statementData.supplier) {
      toast.error("No statement data to print");
      return;
    }
    printStatementHTML({
      type: "Supplier",
      party: statementData.supplier,
      storeSettings,
      transactions: statementData.transactions || [],
      summary: statementData.summary || {},
      dateRange: dateFilter.from && dateFilter.to ? `${dateFilter.from} to ${dateFilter.to}` : "All Time",
      currency: currencySymbol
    });
  };

  return (
    <Page>
      {/* ──────────────── Top Navigation & Header ──────────────── */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8 gap-6">
        <div>
          <div className="flex items-center gap-3">
            {activeTab === "statement" && (
              <button
                onClick={() => setActiveTab("suppliers")}
                className="p-2.5 rounded-2xl bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition shadow-sm"
                title="Back to Suppliers"
              >
                <IconArrowLeft size={20} stroke={iconStroke} />
              </button>
            )}
            <div>
              <h2 className="text-3xl font-black tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
                Accounts Payable <span className="text-red-500 font-bold text-lg">(Vendor Dues)</span>
              </h2>
              <p className="text-gray-500 text-sm mt-0.5">
                {activeTab === "statement"
                  ? `Detailed account ledger & statement for ${selectedSupplierForStatement?.name}`
                  : "Track vendor liabilities, clear purchase bills in FIFO sequence, and download statements"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          <button
            onClick={() => document.getElementById("modal-manual-due").showModal()}
            className="px-5 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-900/40 font-bold text-sm rounded-2xl border border-red-200 dark:border-red-900/50 transition flex items-center gap-2 shadow-sm"
          >
            + Add Manual Due
          </button>

          {activeTab !== "statement" && (
            <div className="join bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 p-1 rounded-2xl shadow-sm">
              <button
                onClick={() => setActiveTab("suppliers")}
                className={`join-item px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition ${
                  activeTab === "suppliers"
                    ? "bg-red-600 text-white shadow"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <IconBuildingStore size={16} /> By Supplier ({supplierDues?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab("purchases")}
                className={`join-item px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition ${
                  activeTab === "purchases"
                    ? "bg-red-600 text-white shadow"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <IconReceipt2 size={16} /> All Bills ({duePurchases?.length || 0})
              </button>
            </div>
          )}

          {activeTab === "statement" && (
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-bold rounded-2xl transition flex items-center gap-1.5 shadow-sm"
              >
                <IconPrinter size={16} /> Print
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 text-xs font-bold rounded-2xl transition flex items-center gap-1.5 shadow-sm shadow-red-200 dark:shadow-none"
              >
                <IconFileDownload size={16} /> PDF Statement
              </button>
              <button
                onClick={handleExportExcel}
                className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-bold rounded-2xl transition flex items-center gap-1.5 shadow-sm shadow-emerald-200 dark:shadow-none"
              >
                <IconFileSpreadsheet size={16} /> Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ──────────────── Key Metrics Summary Cards ──────────────── */}
      {activeTab !== "statement" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {/* Card 1: Total Liabilities */}
          <div className="bg-gradient-to-br from-red-600 via-rose-600 to-red-700 p-6 rounded-[2rem] text-white shadow-xl shadow-red-100 dark:shadow-none relative overflow-hidden">
            <div className="absolute right-4 top-4 text-white/10">
              <IconTrendingDown size={80} />
            </div>
            <div className="flex items-center gap-2 opacity-85 text-xs font-black uppercase tracking-widest mb-2">
              <IconTrendingDown size={18} /> Total Liabilities Due
            </div>
            <div className="text-3xl font-black tracking-tight">
              <span className="text-lg font-normal opacity-80 mr-1">{currencySymbol}</span>
              {metrics.totalLiabilities.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] text-red-100 font-medium mt-2">Outstanding balance to all suppliers</p>
          </div>

          {/* Card 2: Total Invoiced Purchases */}
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-black uppercase tracking-widest mb-2">
              <IconReceipt2 size={18} className="text-blue-500" /> Total Purchases Billed
            </div>
            <div className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
              <span className="text-lg font-normal text-gray-400 mr-1">{currencySymbol}</span>
              {metrics.totalPurchased.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] text-gray-400 font-medium mt-2">Cumulative purchase bills value</p>
          </div>

          {/* Card 3: Total Paid */}
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-black uppercase tracking-widest mb-2">
              <IconCash size={18} className="text-emerald-500" /> Settled to Vendors
            </div>
            <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
              <span className="text-lg font-normal text-gray-400 mr-1">{currencySymbol}</span>
              {metrics.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] text-gray-400 font-medium mt-2">Total payments issued so far</p>
          </div>

          {/* Card 4: Vendors with Dues */}
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-black uppercase tracking-widest mb-2">
              <IconTruckDelivery size={18} className="text-amber-500" /> Vendors with Dues
            </div>
            <div className="text-3xl font-black text-amber-600 dark:text-amber-400 tracking-tight">
              {metrics.suppliersWithDue} <span className="text-base font-normal text-gray-400">parties</span>
            </div>
            <p className="text-[11px] text-gray-400 font-medium mt-2">Suppliers with active open balances</p>
          </div>
        </div>
      )}

      {/* ──────────────── Search & Date Filter Bar ──────────────── */}
      {activeTab !== "statement" && (
        <div className="flex flex-col md:flex-row flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center bg-white dark:bg-restro-gray px-4 py-2.5 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex-1 min-w-[280px] max-w-md">
            <IconSearch size={20} className="text-gray-400 mr-3" />
            <input
              type="text"
              placeholder="Search by supplier name, phone, or bill #..."
              className="bg-transparent border-none focus:ring-0 text-sm w-full outline-none"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSupplierPage(1);
                setBillsPage(1);
              }}
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setSupplierPage(1);
                  setBillsPage(1);
                }}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <IconX size={16} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={selectedSupplierForStatement?.id || ""}
              onChange={(e) => {
                const s = supplierList.find(sup => String(sup.id) === String(e.target.value));
                if (s) {
                  btnOpenStatement(s);
                }
              }}
              className="select select-sm select-bordered rounded-2xl bg-white dark:bg-restro-gray text-xs font-semibold h-11"
            >
              <option value="">Quick Ledger: Select any vendor...</option>
              {supplierList.map((s) => (
                <option key={s.id} value={s.id}>{s.name} {s.phone ? `(${s.phone})` : ""}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center bg-white dark:bg-restro-gray px-3 py-2 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm gap-2 text-xs">
            <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px] pl-2">Date:</span>
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => {
                setDateFilter({ ...dateFilter, from: e.target.value });
                setSupplierPage(1);
                setBillsPage(1);
              }}
              className="bg-transparent border-none focus:ring-0 text-xs py-1.5 px-2 outline-none font-bold"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateFilter.to}
              onChange={(e) => {
                setDateFilter({ ...dateFilter, to: e.target.value });
                setSupplierPage(1);
                setBillsPage(1);
              }}
              className="bg-transparent border-none focus:ring-0 text-xs py-1.5 px-2 outline-none font-bold"
            />
            {(dateFilter.from || dateFilter.to) && (
              <button
                onClick={() => {
                  setDateFilter({ from: "", to: "" });
                  setSupplierPage(1);
                  setBillsPage(1);
                }}
                className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-bold flex items-center gap-1 transition text-xs"
              >
                <IconX size={12} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* ──────────────── VIEW TAB 1: BY SUPPLIER (DEFAULT) ──────────────── */}
      {activeTab === "suppliers" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {isLoadingSupplierDues && (
              <div className="col-span-full py-24 text-center">
                <span className="loading loading-spinner loading-lg text-red-600"></span>
                <p className="text-gray-400 text-xs mt-3 font-bold uppercase tracking-widest">Loading Supplier Payables...</p>
              </div>
            )}

            {!isLoadingSupplierDues && (!supplierDues || supplierDues.length === 0) && (
              <div className="col-span-full py-28 text-center bg-white dark:bg-restro-gray border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-[2.5rem]">
                <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-950/40 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-500">
                  <IconCheck size={36} stroke={2.5} />
                </div>
                <h4 className="text-xl font-black text-gray-800 dark:text-gray-200">Zero Outstanding Liabilities!</h4>
                <p className="text-gray-400 text-sm mt-1 max-w-sm mx-auto">
                  All suppliers and vendor bills are completely cleared. No open dues found.
                </p>
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-2">Want to inspect a past ledger?</p>
                  <select
                    onChange={(e) => {
                      const s = supplierList.find(sup => String(sup.id) === String(e.target.value));
                      if (s) btnOpenStatement(s);
                    }}
                    className="select select-sm select-bordered rounded-xl"
                  >
                    <option value="">Select Vendor to View Ledger...</option>
                    {supplierList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            {paginatedSupplierDues?.map((sup) => {
              const dueAmt = Number(String(sup.total_due).replace(/,/g, "")) || 0;
              const purchasedAmt = Number(String(sup.total_purchased).replace(/,/g, "")) || 0;
              const paidAmt = Number(String(sup.total_paid).replace(/,/g, "")) || 0;

              return (
                <div
                  key={sup.id}
                  className="relative overflow-hidden bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 rounded-3xl shadow-sm flex flex-col justify-between"
                >
                  <div className="h-1.5 bg-red-600"></div>

                  <div className="p-6">
                    {/* Top line with Avatar and Tag */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center font-black text-lg shadow-sm border border-red-100 dark:border-red-900/30">
                          {sup.name?.[0]?.toUpperCase() || "S"}
                        </div>
                        <div>
                          <h4 className="text-lg font-black text-gray-900 dark:text-white leading-tight truncate max-w-[170px]" title={sup.name}>
                            {sup.name}
                          </h4>
                          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                            <IconPhone size={12} /> {sup.phone || "No phone"}
                          </p>
                        </div>
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300 border border-red-100 dark:border-red-900/30">
                        {sup.unpaid_bills_count || 0} Bills Due
                      </span>
                    </div>

                    {/* Supplier details pill */}
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-5 flex flex-wrap gap-x-3 gap-y-1">
                      {sup.pan_no && (
                        <span className="flex items-center gap-1 font-semibold">
                          <IconId size={13} className="text-gray-400" /> PAN: {sup.pan_no}
                        </span>
                      )}
                      {sup.address && (
                        <span className="truncate max-w-[200px]" title={sup.address}>
                          {sup.address}
                        </span>
                      )}
                    </div>

                    {/* Financial Breakdown card */}
                    <div className="bg-gray-50 dark:bg-gray-900/40 p-4 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-2 mb-4">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Total Invoiced</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {currencySymbol} {purchasedAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Total Settled</span>
                        <span className="font-bold text-emerald-600">
                          {currencySymbol} {paidAmt.toFixed(2)}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-800 flex justify-between items-center">
                        <span className="text-[11px] font-black uppercase tracking-wider text-red-600">
                          Outstanding Due
                        </span>
                        <span className="text-xl font-black text-red-600">
                          {currencySymbol} {dueAmt.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions bottom bar */}
                  <div className="p-6 pt-0 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => btnOpenStatement(sup)}
                      className="py-3 px-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <IconReceipt2 size={16} /> Statement
                    </button>
                    <button
                      onClick={() => btnShowBulkPayModal(sup)}
                      className="py-3 px-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                    >
                      <IconCash size={16} /> Settle (FIFO)
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Supplier Tab Pagination controls */}
          {supplierDues?.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 border border-gray-100 dark:border-gray-800 rounded-2xl bg-white dark:bg-restro-gray">
              <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                <span>
                  Showing {((safeSupplierPage - 1) * supplierPageSize) + 1} to {Math.min(safeSupplierPage * supplierPageSize, supplierDues.length)} of {supplierDues.length} vendors
                </span>
                <select
                  value={supplierPageSize}
                  onChange={(e) => {
                    setSupplierPageSize(Number(e.target.value));
                    setSupplierPage(1);
                  }}
                  className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
                >
                  <option value={6}>6 / page</option>
                  <option value={12}>12 / page</option>
                  <option value={24}>24 / page</option>
                  <option value={60}>60 / page</option>
                </select>
              </div>
              <div className="join">
                <button
                  className="join-item btn btn-xs"
                  disabled={safeSupplierPage === 1}
                  onClick={() => setSupplierPage(1)}
                  title="First Page"
                >
                  <IconChevronsLeft size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeSupplierPage === 1}
                  onClick={() => setSupplierPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <IconChevronLeft size={14} />
                </button>
                <button className="join-item btn btn-xs no-animation font-semibold">
                  Page {safeSupplierPage} of {totalSupplierPages}
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeSupplierPage >= totalSupplierPages}
                  onClick={() => setSupplierPage((p) => Math.min(totalSupplierPages, p + 1))}
                  title="Next Page"
                >
                  <IconChevronRight size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeSupplierPage >= totalSupplierPages}
                  onClick={() => setSupplierPage(totalSupplierPages)}
                  title="Last Page"
                >
                  <IconChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──────────────── VIEW TAB 2: ALL INDIVIDUAL PURCHASE BILLS ──────────────── */}
      {activeTab === "purchases" && (
        <div className="bg-white dark:bg-restro-gray rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-800 text-[11px] font-black uppercase text-gray-400 tracking-wider">
                  <th className="py-4 px-6">Bill / Invoice #</th>
                  <th>Supplier Name</th>
                  <th>Bill Date</th>
                  <th>Total Amount</th>
                  <th>Paid Amount</th>
                  <th>Due Amount</th>
                  <th>Status</th>
                  <th className="text-right px-6">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                {(!paginatedDuePurchases || paginatedDuePurchases.length === 0) && (
                  <tr>
                    <td colSpan={8} className="py-20 text-center text-gray-400">
                      No unpaid purchase bills found matching your filter.
                    </td>
                  </tr>
                )}
                {paginatedDuePurchases.map((p) => {
                  const total = (Number(String(p.total_amount).replace(/,/g, "")) || 0) - (Number(String(p.discount_amount).replace(/,/g, "")) || 0);
                  const paid = Number(String(p.paid_amount).replace(/,/g, "")) || 0;
                  const due = Number(String(p.due_amount).replace(/,/g, "")) || 0;

                  return (
                    <tr key={p.id} className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors">
                      <td className="py-4 px-6 font-mono font-bold text-xs">
                        <span className={`px-2 py-1 rounded-md ${
                          p.purchase_invoice_no === 'OPENING-BALANCE'
                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}>
                          {p.purchase_invoice_no || `PUR-#${p.id}`}
                        </span>
                      </td>
                      <td className="font-bold text-gray-900 dark:text-white">
                        {p.supplier_name || "Supplier"}
                      </td>
                      <td className="text-xs text-gray-500">
                        {p.purchase_date ? new Date(p.purchase_date).toLocaleDateString() : "-"}
                      </td>
                      <td className="font-semibold text-gray-700 dark:text-gray-300">
                        {currencySymbol} {total.toFixed(2)}
                      </td>
                      <td className="text-emerald-600 font-bold">
                        {currencySymbol} {paid.toFixed(2)}
                      </td>
                      <td className="text-red-600 font-black text-base">
                        {currencySymbol} {due.toFixed(2)}
                      </td>
                      <td>
                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          paid > 0
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'
                        }`}>
                          {paid > 0 ? 'Partial' : 'Unpaid'}
                        </span>
                      </td>
                      <td className="text-right px-6">
                        <button
                          onClick={() => btnShowSinglePayModal(p)}
                          className="px-3.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
                        >
                          Pay Bill
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Bills Tab Pagination controls */}
          {duePurchases?.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-restro-gray">
              <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                <span>
                  Showing {((safeBillsPage - 1) * billsPageSize) + 1} to {Math.min(safeBillsPage * billsPageSize, duePurchases.length)} of {duePurchases.length} bills
                </span>
                <select
                  value={billsPageSize}
                  onChange={(e) => {
                    setBillsPageSize(Number(e.target.value));
                    setBillsPage(1);
                  }}
                  className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
              <div className="join">
                <button
                  className="join-item btn btn-xs"
                  disabled={safeBillsPage === 1}
                  onClick={() => setBillsPage(1)}
                  title="First Page"
                >
                  <IconChevronsLeft size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeBillsPage === 1}
                  onClick={() => setBillsPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <IconChevronLeft size={14} />
                </button>
                <button className="join-item btn btn-xs no-animation font-semibold">
                  Page {safeBillsPage} of {totalBillsPages}
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeBillsPage >= totalBillsPages}
                  onClick={() => setBillsPage((p) => Math.min(totalBillsPages, p + 1))}
                  title="Next Page"
                >
                  <IconChevronRight size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeBillsPage >= totalBillsPages}
                  onClick={() => setBillsPage(totalBillsPages)}
                  title="Last Page"
                >
                  <IconChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──────────────── VIEW TAB 3: SUPPLIER STATEMENT & LEDGER ──────────────── */}
      {activeTab === "statement" && selectedSupplierForStatement && (
        <div className="space-y-6">
          {/* Supplier Header Profile Card */}
          <div className="bg-white dark:bg-restro-gray rounded-[2rem] border border-gray-100 dark:border-gray-800 p-6 md:p-8 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 text-white flex items-center justify-center font-black text-2xl shadow-md">
                {selectedSupplierForStatement.name?.[0]?.toUpperCase() || "S"}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white">
                    {selectedSupplierForStatement.name}
                  </h3>
                  <span className="text-xs px-2.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300 font-bold rounded-full">
                    Vendor Ledger
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {selectedSupplierForStatement.phone && (
                    <span className="flex items-center gap-1 font-semibold">
                      <IconPhone size={13} /> {selectedSupplierForStatement.phone}
                    </span>
                  )}
                  {selectedSupplierForStatement.pan_no && (
                    <span className="flex items-center gap-1 font-semibold">
                      <IconId size={13} /> PAN/VAT: {selectedSupplierForStatement.pan_no}
                    </span>
                  )}
                  {selectedSupplierForStatement.email && (
                    <span className="flex items-center gap-1">
                      <IconMail size={13} /> {selectedSupplierForStatement.email}
                    </span>
                  )}
                  {selectedSupplierForStatement.address && (
                    <span>{selectedSupplierForStatement.address}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Live Balance & Quick Action */}
            <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 pt-4 md:pt-0 border-gray-100 dark:border-gray-800">
              <div className="text-right">
                <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Net Outstanding Due</span>
                <div className="text-2xl font-black text-red-600">
                  {currencySymbol} {(Number(statementData?.summary?.net_due) || 0).toFixed(2)}
                </div>
              </div>
              <button
                onClick={() => btnShowBulkPayModal(selectedSupplierForStatement)}
                className="py-3 px-5 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold text-sm flex items-center gap-2 transition shadow-lg shadow-red-200 dark:shadow-none"
              >
                <IconCash size={18} /> Settle (FIFO)
              </button>
            </div>
          </div>

          {/* Statement Transaction Ledger Table */}
          <div className="bg-white dark:bg-restro-gray rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <div>
                <h4 className="font-black text-lg text-gray-900 dark:text-white">Transaction Statement</h4>
                <p className="text-gray-400 text-xs">Chronological record of purchase bills, payments, and running liability balance</p>
              </div>
              <div className="text-xs text-gray-500 font-bold">
                Total Transactions: {statementData?.transactions?.length || 0}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="table w-full text-left text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-800 text-[11px] font-black uppercase text-gray-400 tracking-wider">
                    <th className="py-4 px-6">Date</th>
                    <th>Voucher / Type</th>
                    <th>Reference #</th>
                    <th>Description</th>
                    <th className="text-right">Debit (Bill)</th>
                    <th className="text-right">Credit (Paid)</th>
                    <th className="text-right px-6">Running Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {isLoadingStatement && (
                    <tr>
                      <td colSpan={7} className="py-16 text-center text-gray-400 font-bold">
                        <span className="loading loading-spinner text-red-600"></span>
                        <p className="mt-2 text-xs">Loading Statement...</p>
                      </td>
                    </tr>
                  )}

                  {!isLoadingStatement && (!statementTransactions || statementTransactions.length === 0) && (
                    <tr>
                      <td colSpan={7} className="py-16 text-center text-gray-400">
                        No transactions recorded for this supplier.
                      </td>
                    </tr>
                  )}

                  {paginatedStatementTransactions?.map((t, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors">
                      <td className="py-3.5 px-6 font-medium text-xs text-gray-600 dark:text-gray-400">
                        {t.date ? new Date(t.date).toLocaleDateString() : "-"}
                      </td>
                      <td>
                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                          t.credit > 0
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : (t.type === 'Opening Balance'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                                : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300')
                        }`}>
                          {t.type}
                        </span>
                      </td>
                      <td className="font-mono text-xs font-bold text-gray-800 dark:text-gray-200">
                        {t.ref_no || "-"}
                      </td>
                      <td className="text-xs text-gray-600 dark:text-gray-400 max-w-xs truncate" title={t.description}>
                        {t.description || "-"}
                      </td>
                      <td className="text-right font-semibold text-gray-800 dark:text-gray-200">
                        {t.debit > 0 ? `${currencySymbol} ${Number(t.debit).toFixed(2)}` : "-"}
                      </td>
                      <td className="text-right font-bold text-emerald-600">
                        {t.credit > 0 ? `${currencySymbol} ${Number(t.credit).toFixed(2)}` : "-"}
                      </td>
                      <td className="text-right px-6 font-black text-gray-900 dark:text-white">
                        {currencySymbol} {Number(t.balance || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Statement Pagination controls */}
            {statementTransactions?.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-restro-gray">
                <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                  <span>
                    Showing {((safeStatementPage - 1) * statementPageSize) + 1} to {Math.min(safeStatementPage * statementPageSize, statementTransactions.length)} of {statementTransactions.length} entries
                  </span>
                  <select
                    value={statementPageSize}
                    onChange={(e) => {
                      setStatementPageSize(Number(e.target.value));
                      setStatementPage(1);
                    }}
                    className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
                  >
                    <option value={15}>15 / page</option>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
                <div className="join">
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage === 1}
                    onClick={() => setStatementPage(1)}
                    title="First Page"
                  >
                    <IconChevronsLeft size={14} />
                  </button>
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage === 1}
                    onClick={() => setStatementPage((p) => Math.max(1, p - 1))}
                    title="Previous Page"
                  >
                    <IconChevronLeft size={14} />
                  </button>
                  <button className="join-item btn btn-xs no-animation font-semibold">
                    Page {safeStatementPage} of {totalStatementPages}
                  </button>
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage >= totalStatementPages}
                    onClick={() => setStatementPage((p) => Math.min(totalStatementPages, p + 1))}
                    title="Next Page"
                  >
                    <IconChevronRight size={14} />
                  </button>
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage >= totalStatementPages}
                    onClick={() => setStatementPage(totalStatementPages)}
                    title="Last Page"
                  >
                    <IconChevronsRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Statement Summary Footer Box */}
            {statementData?.summary && (
              <div className="p-6 bg-gray-50 dark:bg-gray-900/60 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-xs text-gray-400">
                  Showing complete transaction ledger up to current date.
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-gray-400 text-xs mr-2">Total Billed:</span>
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                      {currencySymbol} {Number(statementData.summary.total_purchases).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs mr-2">Total Paid:</span>
                    <span className="font-bold text-emerald-600">
                      {currencySymbol} {Number(statementData.summary.total_paid).toFixed(2)}
                    </span>
                  </div>
                  <div className="pl-4 border-l border-gray-200 dark:border-gray-700">
                    <span className="text-gray-400 text-xs mr-2 font-bold uppercase">Net Payable:</span>
                    <span className="font-black text-red-600 text-base">
                      {currencySymbol} {Number(statementData.summary.net_due).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ──────────────── MODAL 1: BULK FIFO PAYMENT MODAL ──────────────── */}
      <dialog id="modal-bulk-pay-fifo" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-rose-700 p-6 text-white relative shrink-0">
            <h3 className="text-2xl font-black mb-0.5">Settle Vendor Dues (FIFO)</h3>
            <p className="text-red-100 text-xs">
              Payments automatically clear unpaid bills starting from the oldest invoice date
            </p>
            <button
              onClick={() => document.getElementById("modal-bulk-pay-fifo").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto flex-1 overscroll-contain">
            {/* Selected Supplier Banner */}
            {selectedSupplierForBulkPay && (
              <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-2xl border border-red-100 dark:border-red-900/40 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Vendor</p>
                  <p className="font-black text-base text-gray-900 dark:text-white">{selectedSupplierForBulkPay.name}</p>
                  <p className="text-xs text-gray-500">{selectedSupplierForBulkPay.phone || "No phone"}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Current Due</p>
                  <p className="text-red-600 font-black text-xl">
                    {currencySymbol} {(Number(selectedSupplierForBulkPay.total_due) || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {/* Amount input & Quick helpers */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-gray-500">
                  Payment Amount
                </label>
                {selectedSupplierForBulkPay && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPayAmount(Number(selectedSupplierForBulkPay.total_due || 0).toFixed(2))}
                      className="text-[11px] font-bold text-red-600 hover:underline"
                    >
                      Pay Full ({currencySymbol}{Number(selectedSupplierForBulkPay.total_due || 0).toFixed(2)})
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-red-500 transition">
                <span className="px-4 py-3.5 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-black text-lg select-none flex items-center justify-center shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full bg-transparent py-3.5 px-4 font-black text-2xl text-gray-900 dark:text-white outline-none"
                />
              </div>
            </div>

            {/* ─── LIVE FIFO ALLOCATION PREVIEW BOX ─── */}
            {fifoAllocations.length > 0 && (
              <div className="bg-slate-50 dark:bg-gray-900/80 p-4 rounded-2xl border border-slate-200 dark:border-gray-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                    <IconLayersSubtract size={15} className="text-red-500" /> FIFO Bill Allocation Preview
                  </span>
                  <span className="text-[10px] text-gray-400">Oldest to newest</span>
                </div>

                <div className="max-h-44 overflow-y-auto space-y-1.5 text-xs pr-1">
                  {fifoAllocations.map((alloc, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
                    >
                      <div>
                        <div className="flex items-center gap-1.5 font-bold">
                          <span className="font-mono text-gray-800 dark:text-gray-200">{alloc.invoice_no}</span>
                          <span className="text-[10px] text-gray-400">({new Date(alloc.date).toLocaleDateString()})</span>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          Due: {currencySymbol}{alloc.original_due.toFixed(2)} → Paying: <span className="font-bold text-emerald-600">{currencySymbol}{alloc.allocated.toFixed(2)}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        {alloc.is_cleared ? (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 rounded-md">
                            Cleared ✓
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-amber-600">
                            Rem: {currencySymbol}{alloc.remaining_due.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Remaining Supplier Balance Summary */}
                <div className="pt-2 border-t border-slate-200 dark:border-gray-700 flex justify-between text-xs font-bold text-gray-600 dark:text-gray-300">
                  <span>Remaining Vendor Liability:</span>
                  <span className="text-red-600 font-black">
                    {currencySymbol} {Math.max(0, (Number(selectedSupplierForBulkPay?.total_due || 0) - (parseFloat(payAmount) || 0))).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Date & Payment Method */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Payment Date
                </label>
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold focus:ring-2 ring-red-500 outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Payment Method
                </label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold focus:ring-2 ring-red-500 outline-none"
                >
                  {paymentTypes?.filter((pt) => pt.is_active).map((pt) => (
                    <option key={pt.id} value={pt.title}>{pt.title}</option>
                  ))}
                  {(!paymentTypes || paymentTypes.filter((pt) => pt.is_active).length === 0) && (
                    <>
                      <option value="Cash">Cash</option>
                      <option value="Bank">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Online">Online Transfer</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Notes / Reference */}
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Reference / Notes
              </label>
              <textarea
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                placeholder="Cheque #, Bank UTR, Transaction Ref..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs focus:ring-2 ring-red-500 outline-none h-16 resize-none"
              ></textarea>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-bulk-pay-fifo").close()}
                className="flex-1 py-3.5 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200 dark:hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingPay}
                onClick={btnProcessBulkFIFOPayment}
                className="flex-[1.5] py-3.5 font-bold text-xs text-white bg-red-600 hover:bg-red-700 rounded-2xl transition shadow-lg shadow-red-200 dark:shadow-none flex items-center justify-center gap-2"
              >
                {isSubmittingPay ? <span className="loading loading-spinner loading-xs"></span> : <IconCheck size={16} />}
                Confirm Settlement
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ──────────────── MODAL 2: SINGLE BILL PAYMENT MODAL ──────────────── */}
      <dialog id="modal-pay-due" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md max-h-[90vh] flex flex-col">
          <div className="bg-gray-900 p-6 text-white relative shrink-0">
            <h3 className="text-xl font-black mb-0.5">Pay Purchase Bill</h3>
            <p className="text-gray-400 text-xs">Settle individual vendor bill</p>
            <button
              onClick={() => document.getElementById("modal-pay-due").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 overscroll-contain">
            {selectedPurchase && (
              <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-2xl border border-red-100 dark:border-red-900/30 flex justify-between items-center">
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Vendor</p>
                  <p className="font-bold text-sm text-gray-900 dark:text-white">{selectedPurchase.supplier_name}</p>
                  <p className="text-xs text-gray-500 font-mono">{selectedPurchase.purchase_invoice_no || `PUR-#${selectedPurchase.id}`}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Due Amount</p>
                  <p className="text-red-600 font-black text-lg">
                    {currencySymbol} {(Number(selectedPurchase.due_amount) || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Amount to Pay
              </label>
              <div className="flex items-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-red-500 transition">
                <span className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-black text-base select-none flex items-center justify-center shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  step="any"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full bg-transparent py-3 px-4 font-black text-xl text-gray-900 dark:text-white outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Date</label>
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-3 text-xs font-bold outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Method</label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-3 text-xs font-bold outline-none"
                >
                  {paymentTypes?.filter((pt) => pt.is_active).map((pt) => (
                    <option key={pt.id} value={pt.title}>{pt.title}</option>
                  ))}
                  {(!paymentTypes || paymentTypes.filter((pt) => pt.is_active).length === 0) && (
                    <>
                      <option value="Cash">Cash</option>
                      <option value="Bank">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Notes</label>
              <textarea
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                placeholder="Transaction details..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2 px-3 text-xs outline-none h-16 resize-none"
              ></textarea>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-pay-due").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingPay}
                onClick={btnProcessSinglePayment}
                className="flex-[1.5] py-3 font-bold text-xs text-white bg-red-600 hover:bg-red-700 rounded-2xl transition shadow-md"
              >
                {isSubmittingPay ? <span className="loading loading-spinner loading-xs"></span> : "Confirm Payment"}
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ──────────────── MODAL 3: MANUAL DUE ENTRY MODAL ──────────────── */}
      <dialog id="modal-manual-due" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md max-h-[90vh] flex flex-col">
          <div className="bg-gradient-to-r from-red-600 to-rose-700 p-6 text-white relative shrink-0">
            <h3 className="text-xl font-black mb-0.5">Add Manual Vendor Due</h3>
            <p className="text-red-100 text-xs">Record an adjustment or opening balance liability</p>
            <button
              onClick={() => document.getElementById("modal-manual-due").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 overscroll-contain">
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Supplier</label>
              <select
                value={manualDueForm.supplier_id}
                onChange={(e) => setManualDueForm({ ...manualDueForm, supplier_id: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold outline-none"
              >
                <option value="">Select Supplier</option>
                {supplierList.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.phone || "No phone"})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Due Amount</label>
              <div className="flex items-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-red-500 transition">
                <span className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-black text-sm select-none flex items-center justify-center shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  step="any"
                  value={manualDueForm.amount}
                  onChange={(e) => setManualDueForm({ ...manualDueForm, amount: e.target.value })}
                  className="w-full bg-transparent py-3 px-4 font-black text-lg text-gray-900 dark:text-white outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Date</label>
              <input
                type="date"
                value={manualDueForm.date}
                onChange={(e) => setManualDueForm({ ...manualDueForm, date: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Reference / Description</label>
              <input
                type="text"
                value={manualDueForm.notes}
                onChange={(e) => setManualDueForm({ ...manualDueForm, notes: e.target.value })}
                placeholder="Reason or invoice reference..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs outline-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-manual-due").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={btnProcessManualDue}
                className="flex-[1.5] py-3 font-bold text-xs text-white bg-red-600 hover:bg-red-700 rounded-2xl transition shadow-md"
              >
                Save Due
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </Page>
  );
}
